import React, { useState, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';
import { allocateAsset } from '../../../../models/assetsModel';
import { getUsers } from '../../../../models/userModel';
import Modal from '../../../../components/common/Modal';
import Button from '../../../../components/common/Button';

function AllocateAssetModal({ isOpen, onClose, onSuccess, asset }) {
  const [formData, setFormData] = useState({
    asset_id: '',
    allocate_staff_id: '',
    asset_allocation_date: new Date().toISOString().split('T')[0],
    asset_allocation_notes: ''
  });
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (isOpen && asset) {
      setFormData(prev => ({
        ...prev,
        asset_id: asset.asset_id
      }));
      fetchUsers();
    }
  }, [isOpen, asset]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      console.log('Fetching users for allocation modal...');
      
      let allUsers = [];
      let currentPage = 1;
      let totalPages = 1;
      
      // Fetch all pages of users
      do {
        const params = new URLSearchParams();
        params.append('page', currentPage.toString());
        params.append('pageSize', '100'); // Use reasonable page size
        const response = await getUsers(params);
        
        console.log(`Page ${currentPage} response:`, response);
        
        if (response && response.data && response.data.rows && Array.isArray(response.data.rows)) {
          allUsers = [...allUsers, ...response.data.rows];
          totalPages = response.data.meta?.totalPages || 1;
        } else {
          break;
        }
        
        currentPage++;
      } while (currentPage <= totalPages);
      
      console.log('Total users fetched:', allUsers.length);
      setUsers(allUsers);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Failed to load users');
      setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.allocate_staff_id) {
      toast.error('Please select a user to allocate the asset to');
      return;
    }

    try {
      setIsSubmitting(true);
      const result = await allocateAsset(formData);
      
      if (result) {
        onSuccess();
        resetForm();
      }
    } catch (error) {
      console.error('Error allocating asset:', error);
      toast.error('Failed to allocate asset');
    } finally {
      setIsSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      asset_id: '',
      allocate_staff_id: '',
      asset_allocation_date: new Date().toISOString().split('T')[0],
      asset_allocation_notes: ''
    });
    setSearchTerm('');
  };

  const handleClose = () => {
    resetForm();
    setSearchTerm('');
    setIsDropdownOpen(false);
    onClose();
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (loading) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} title="Allocate Asset">
        <div className="flex justify-center items-center h-32">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title="Allocate Asset">
      <div className="space-y-4">
        {/* Asset Information */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <h3 className="text-lg font-medium text-gray-800 mb-2">Asset Information</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Asset Name</label>
              <div className="mt-1 text-sm text-gray-900">{asset?.asset_name || 'N/A'}</div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Asset ID</label>
              <div className="mt-1 text-sm text-gray-900">{asset?.asset_id || 'N/A'}</div>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Allocate To *
            </label>
            <select
              name="allocate_staff_id"
              value={formData.allocate_staff_id}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            >
              <option value="">Select User</option>
              {users.map((user) => (
                <option key={user.user_id} value={user.user_id}>
                  {user.user_full_name} ({user.user_email})
                </option>
              ))}
            </select>
            <div className="text-sm text-gray-500 mt-1">
              {users.length > 0 ? `${users.length} users available (all pages loaded)` : 'No users found'}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Allocation Date
            </label>
            <input
              type="date"
              name="asset_allocation_date"
              value={formData.asset_allocation_date}
              onChange={handleInputChange}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Notes
            </label>
            <textarea
              name="asset_allocation_notes"
              value={formData.asset_allocation_notes}
              onChange={handleInputChange}
              rows={3}
              className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Optional allocation notes..."
            />
          </div>

          <div className="flex justify-end space-x-3 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Allocating...' : 'Allocate Asset'}
            </Button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default AllocateAssetModal;
